import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Copy, Plus, Lightbulb, RefreshCw } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { toast } from 'sonner';
import { MairyIcon } from './legendary-icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const MaryChat = ({ 
  projectId, 
  sectionKey, 
  subElementKey,
  context = '',
  onInsert,
  studioType = 'community' // 'community' or 'startup'
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset messages when section changes
  useEffect(() => {
    setMessages([]);
  }, [sectionKey, subElementKey]);

  const sendMessage = async (customPrompt = null) => {
    const promptText = customPrompt || input.trim();
    if (!promptText) return;

    const userMessage = { role: 'user', content: promptText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptText,
          project_id: projectId,
          section_key: sectionKey,
          sub_element_key: subElementKey,
          context: context,
          studio_type: studioType
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('M.ai.ry is taking a moment. Please try again.');
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleAddToCharter = (text) => {
    if (onInsert) {
      onInsert(text);
    }
  };

  // Suggested prompts based on context
  const suggestedPrompts = [
    "What questions should we be asking about this?",
    "What patterns do you see that might lead to collapse?",
    "Help us think more deeply about this",
    "What are we missing?"
  ];

  return (
    <Card className="h-full flex flex-col" data-testid="mary-chat">
      <CardHeader className="pb-3 border-b border-[var(--muted)]">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-full bg-[var(--primary)] flex items-center justify-center">
            <MairyIcon size={18} className="text-white" />
          </div>
          M.ai.ry
          <span className="text-xs font-normal text-[var(--fg)]/50 ml-auto">AI Guide</span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px] max-h-[400px]">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <MairyIcon className="w-12 h-12 mx-auto mb-4 text-[var(--primary)] opacity-60" />
              <p className="text-sm text-[var(--fg)]/60 mb-4">
                I'm M.ai.ry, your guide through the compass journey.
                Ask me anything about building your community charter.
              </p>
              <div className="space-y-2">
                {suggestedPrompts.slice(0, 2).map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(prompt)}
                    className="block w-full text-left text-xs p-2 rounded-lg bg-[var(--muted)] hover:bg-[var(--primary)]/10 text-[var(--fg)]/70 transition-colors"
                  >
                    <Lightbulb size={12} className="inline mr-2 text-[var(--accent)]" />
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-[var(--primary)] text-white rounded-br-md'
                        : 'bg-[var(--sky)] text-[var(--fg)] rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    
                    {/* Action buttons for assistant messages */}
                    {message.role === 'assistant' && (
                      <div className="flex gap-2 mt-3 pt-2 border-t border-[var(--fg)]/10">
                        <button
                          onClick={() => handleCopyToClipboard(message.content)}
                          className="flex items-center gap-1 text-xs text-[var(--fg)]/60 hover:text-[var(--fg)] transition-colors"
                          data-testid="copy-message-button"
                        >
                          <Copy size={12} />
                          Copy
                        </button>
                        <button
                          onClick={() => handleAddToCharter(message.content)}
                          className="flex items-center gap-1 text-xs text-[var(--primary)] hover:text-[var(--primary-600)] font-medium transition-colors"
                          data-testid="add-to-charter-button"
                        >
                          <Plus size={12} />
                          Add to Charter
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          
          {/* Loading indicator */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-[var(--sky)] rounded-2xl rounded-bl-md px-4 py-3">
                <div className="typing flex gap-1">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="p-4 border-t border-[var(--muted)]">
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask M.ai.ry for guidance..."
              className="flex-1 resize-none rounded-xl border border-[var(--muted)] bg-[var(--card)] px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)] min-h-[44px] max-h-[120px]"
              rows={1}
              data-testid="mary-chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              className="self-end"
              data-testid="mary-send-button"
            >
              {isLoading ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaryChat;
